import { Checkbox, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import WKT from 'ol/format/WKT';
import { Point } from 'ol/geom';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

import { useGet3DProjectionData, useGet3DProjectionConfigList } from '../api/useProjections3DConfigController';
import { Projection3DColorValueMapItem } from '../models/types/AttributeColorValueMap';
import { Layer3DProjectionConfig } from '../models/types/Layer3DProjectionConfig';
import { use3DGridStore } from '../stores/use3DGridStore';
import { use3DSceneStore } from '../stores/use3DSceneStore';
import { use3DViewerStore } from '../stores/use3DViewerStore';
import { GeopostProjectionGroup } from '../threejs/group/GeopostProjectionMesh';
import { GeopostThreeJsConstants } from '../threejs/utils/GeopostThreeJsConstants';
import { RaycasterEventFactory } from '../threejs/utils/RaycasterEventFactory';
import { scaleGeometry } from '../threejs/utils/ScaleUtils';

export const Projections3DListRow = ({ projectionConfig } : Projection3DListRowProps) => {
    const projectionGroupsRef = useRef<GeopostProjectionGroup[]>([]);

    const scene = use3DSceneStore(state => state.scene);

    const {
        mainFeatureGeomWkt,
        mainFeatureSrid,
        mainFeatureCentroid,
        sampleInterval,
        domain
    } = use3DViewerStore(state => ({
        mainFeatureCentroid: ([ state.featureCentroidX, state.featureCentroidY ] as [number, number]),
        mainFeatureGeomWkt: state.mainFeatureGeomData?.GeomWKT,
        mainFeatureSrid: state.mainFeatureSrid,
        sampleInterval: state.sampleInterval,
        domain: state.domain,
    }));

    const { mutateAsync: getProjectionData } = useGet3DProjectionData();

    const [ isLoading, setIsLoading ] = useState(false);

    const getItemColorAndSymbolType = useCallback((value: string, colorValueMap: Projection3DColorValueMapItem[]) => {
        const mapItem = colorValueMap.find(item => item.Value === value);
        if (!!mapItem) {
            return [ mapItem.Color, mapItem.SymbolType ];
        }
        else {
            const defaultMap = colorValueMap.find(item => item.Value === '[default-value]');
            return [ defaultMap?.Color ?? '#FFF', defaultMap?.SymbolType ?? 'cylinder' ];
        }
    }, []);

    const handleSelection = useCallback(() => {
        if (!mainFeatureGeomWkt || !mainFeatureSrid || domain === null) {
            return;
        }
        setIsLoading(true);
        getProjectionData({ geomWkt: mainFeatureGeomWkt, geomSrid: mainFeatureSrid, token: projectionConfig.Token, domain})
            .then(projectionItems => {
                projectionItems.forEach(projectionItem => {
                    const itemGeom = (new WKT()).readGeometry(projectionItem.GeomWkt, {
                        dataProjection: 'ESPG:' + mainFeatureSrid
                    });
                    scaleGeometry(itemGeom, mainFeatureCentroid);
                    const itemCoordinates = (itemGeom as Point).getCoordinates();
                    const itemOrigin = Math.abs(projectionItem.ZTop) / sampleInterval / GeopostThreeJsConstants.yDivisionFactor;
                    const itemEnd = Math.abs(projectionItem.ZBottom) / sampleInterval / GeopostThreeJsConstants.yDivisionFactor;

                    const [color, symbolType] = getItemColorAndSymbolType(projectionItem.ColorValue, projectionConfig.ColorValueMap);

                    const itemGroup = new GeopostProjectionGroup(itemOrigin, itemEnd, projectionItem.Width, symbolType, color, itemCoordinates[0], itemCoordinates[1], projectionItem.Label);
                    projectionGroupsRef.current.push(itemGroup);
                    scene?.add(itemGroup);
                });

                const raycasterEventFactory = new RaycasterEventFactory<THREE.Mesh>();
                const intersectionEvent = raycasterEventFactory.addIntersectionEventOnOneOfGroup(
                    projectionGroupsRef.current.map(x => x.mesh),
                    (annotationPoint) => {
                        if (!annotationPoint.parent?.visible) {
                            return;
                        }
                        const annotation = (annotationPoint.parent! as GeopostProjectionGroup);
                        annotation.label.visible = true;
                    },
                    (annotationPoint) => {
                        (annotationPoint.parent! as GeopostProjectionGroup).label.visible = false;
                    }
                );
                scene?.intersectionEvents.push(intersectionEvent);
            })
            .finally(() => setIsLoading(false));
    }, [mainFeatureGeomWkt, mainFeatureSrid, getProjectionData, projectionConfig.Token, projectionConfig.ColorValueMap, scene, mainFeatureCentroid, sampleInterval, getItemColorAndSymbolType]);

    const handleRemoval = useCallback(() => {
        scene?.remove(...projectionGroupsRef.current);
        projectionGroupsRef.current = [];
    }, [scene]);

    return (
        <TableRow>
            <TableCell size='small'>
                <Checkbox disabled={isLoading} onChange={(event, checked) => checked ? handleSelection() : handleRemoval() }/>
            </TableCell>
            <TableCell size='small'>
                {projectionConfig.Title}
            </TableCell>
        </TableRow>
    );
};

export type Projection3DListRowProps = {
    projectionConfig: Layer3DProjectionConfig,
};